/**
 * Async Component Loading Utilities
 * 
 * Provides utilities for efficient async component loading with proper
 * error handling, loading states, and memory optimization.
 */

import { defineAsyncComponent, type AsyncComponentLoader, type Component } from 'vue'

interface AsyncComponentOptions {
  loader: AsyncComponentLoader
  loadingComponent?: Component
  errorComponent?: Component
  delay?: number
  timeout?: number
  suspensible?: boolean
  onError?: (error: Error, retry: () => void, fail: () => void, attempts: number) => any
}

/**
 * Create an optimized async component with proper error handling
 */
export function createAsyncComponent(options: AsyncComponentOptions) {
  return defineAsyncComponent({
    loader: options.loader,
    
    // Loading component shown during async loading
    loadingComponent: options.loadingComponent || {
      template: `
        <div class="async-loading">
          <v-progress-circular indeterminate size="24" width="2"></v-progress-circular>
          <span class="ml-2">Loading...</span>
        </div>
      `
    },
    
    // Error component shown when loading fails
    errorComponent: options.errorComponent || {
      template: `
        <div class="async-error">
          <v-icon color="error">mdi-alert-circle</v-icon>
          <span class="ml-2">Failed to load component</span>
        </div>
      `
    },
    
    // Delay before showing loading component (ms)
    delay: options.delay ?? 200,
    
    // Timeout for loading (ms)
    timeout: options.timeout ?? 30000,
    
    // Enable suspense support
    suspensible: options.suspensible ?? false,
    
    // Error handler with retry logic
    onError: options.onError ?? ((error, retry, fail, attempts) => {
      console.error(`Async component loading failed (attempt ${attempts}):`, error)
      
      // Retry up to 3 times
      if (attempts < 3) {
        console.log(`Retrying component load (attempt ${attempts + 1})...`)
        retry()
      } else {
        console.error('Max retry attempts reached, component loading failed')
        fail()
      }
    })
  })
}

/**
 * Pre-defined async component creators for common components
 */
export const AsyncComponents = {
  /**
   * Budget-related components
   */
  BudgetMainInfo: () => createAsyncComponent({
    loader: () => import('@/components/BudgetMainInfo.vue'),
    delay: 100 // Load quickly since it's critical
  }),
  
  BudgetPage: () => createAsyncComponent({
    loader: () => import('@/components/BudgetPage.vue'),
    delay: 100
  }),
  
  BudgetCreateDialog: () => createAsyncComponent({
    loader: () => import('@/components/BudgetCreateDialog.vue'),
    delay: 300 // Dialog can have higher delay
  }),
  
  /**
   * Invoice-related components
   */
  IncomingInvoicePage: () => createAsyncComponent({
    loader: () => import('@/components/IncomingInvoicePage.vue')
  }),
  
  /**
   * Tour cash register components
   */
  TourCashRegisterIndexPage: () => createAsyncComponent({
    loader: () => import('@/components/TourCashRegisterIndexPage.vue')
  }),
  
  TourCashRegisterReceiptCreatePage: () => createAsyncComponent({
    loader: () => import('@/components/TourCashRegisterReceiptCreatePage.vue')
  }),
  
  /**
   * Wiki components
   */
  WikiAdminPage: () => createAsyncComponent({
    loader: () => import('@/components/WikiAdminPage.vue')
  }),
  
  WikiViewPage: () => createAsyncComponent({
    loader: () => import('@/components/WikiViewPage.vue')
  }),
  
  /**
   * Requirements components
   */
  RequirementPage: () => createAsyncComponent({
    loader: () => import('@/components/RequirementPage.vue')
  }),
  
  RequirementOverviewPage: () => createAsyncComponent({
    loader: () => import('@/components/RequirementOverviewPage.vue')
  })
}

/**
 * Preload critical components for better UX
 */
export async function preloadCriticalComponents(): Promise<void> {
  const criticalComponents = [
    'BudgetMainInfo',
    'BudgetPage'
  ]
  
  const preloadPromises = criticalComponents.map(async (componentName) => {
    try {
      if (componentName === 'BudgetMainInfo') {
        await import('@/components/BudgetMainInfo.vue')
      } else if (componentName === 'BudgetPage') {
        await import('@/components/BudgetPage.vue')
      }
    } catch (error) {
      console.warn(`Failed to preload ${componentName}:`, error)
    }
  })
  
  await Promise.allSettled(preloadPromises)
}

/**
 * Component bundle analyzer - helps identify large components
 */
export function analyzeBundleSize(): Promise<{
  component: string
  estimatedSize: number
}[]> {
  return new Promise((resolve) => {
    const components = Object.keys(AsyncComponents)
    const results: { component: string; estimatedSize: number }[] = []
    
    // Simulate bundle size analysis (in a real app, you'd use webpack-bundle-analyzer)
    components.forEach(component => {
      // Estimate based on component complexity
      let estimatedSize = 15 // Base size in KB
      
      if (component.includes('Budget')) {
        estimatedSize += 25 // Budget components are more complex
      }
      if (component.includes('Admin')) {
        estimatedSize += 20 // Admin components tend to be larger
      }
      if (component.includes('Overview')) {
        estimatedSize += 15 // Overview components have tables/charts
      }
      
      results.push({
        component,
        estimatedSize
      })
    })
    
    resolve(results.sort((a, b) => b.estimatedSize - a.estimatedSize))
  })
}

/**
 * Optimize router configuration for async components
 */
export function optimizeRouterForAsync() {
  return {
    // Enable route-level code splitting
    scrollBehavior(to: any, from: any, savedPosition: any) {
      if (savedPosition) {
        return savedPosition
      }
      // Smooth scroll to top for new routes
      return { top: 0, behavior: 'smooth' }
    },
    
    // Route transition configuration
    meta: {
      transition: 'fade',
      keepAlive: true // Enable keep-alive for better performance
    }
  }
}